/**
 * Tier-1 hints for the head of an aggregate call's argument list.
 *
 * `SELECT COUNT(|` is one of the most-typed positions in SQL and the two
 * tokens that belong there — `*` and `DISTINCT` — are not columns, so the
 * column/table emission alone never surfaces them (`*` isn't a keyword either,
 * so it was missing entirely). This promotes them at the *empty* argument head
 * only; once an argument has been typed the position is an ordinary expression
 * again and the hints stop.
 */

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';
import { scanRegion, wordBefore, scanOptionsFor } from '../context/slotTextScan';
import { parenHeadScanEnd } from '../context/clauseShapeSlot';
import { tokenize } from '../context/tokenizer';

/** Aggregates whose argument may be prefixed with `DISTINCT`, per engine. */
const DISTINCT_AGGREGATES: Partial<Record<DatabaseEngineId, ReadonlySet<string>>> = {
  pgsql: new Set([
    'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'STRING_AGG', 'ARRAY_AGG', 'JSON_AGG',
    'JSONB_AGG', 'JSON_OBJECT_AGG', 'JSONB_OBJECT_AGG', 'BOOL_AND', 'BOOL_OR',
    'BIT_AND', 'BIT_OR', 'EVERY', 'XMLAGG',
  ]),
  mysql: new Set(['COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'GROUP_CONCAT']),
  sqlite: new Set(['COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'GROUP_CONCAT']),
  oracle: new Set(['COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'LISTAGG']),
  mssql: new Set(['COUNT', 'SUM', 'AVG', 'MIN', 'MAX']),
};

export function emitAggregateArgHints(
  out: CompletionCandidate[],
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): void {
  const fn = aggregateAtArgumentHead(text, offset, statementStart, engine);
  if (!fn) return;
  if (fn === 'COUNT') {
    out.push({
      label: '*',
      insertText: '*',
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'count all rows',
    });
  }
  if (DISTINCT_AGGREGATES[engine]?.has(fn)) {
    out.push({
      label: 'DISTINCT',
      insertText: 'DISTINCT',
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'SQL keyword',
    });
  }
}

/** Keyword tails that continue an aggregate's argument list after its first
 *  operand — `GROUP_CONCAT(name ▮` takes `ORDER BY` / `SEPARATOR`, Oracle's
 *  `LISTAGG(x, ',' ▮` takes `ON OVERFLOW`. They are neither columns nor
 *  statement keywords, so nothing else surfaces them here. */
type AggregateTail = {
  readonly keywords: readonly string[];
  /** The tail follows a separator argument, so it is illegal until the call's
   *  comma has been typed (`STRING_AGG(x, ',' ▮)`). */
  readonly afterComma?: true;
};

const AGGREGATE_ARG_TAILS: Partial<
  Record<DatabaseEngineId, Readonly<Record<string, AggregateTail>>>
> = {
  pgsql: {
    STRING_AGG: { keywords: ['ORDER BY'], afterComma: true },
    ARRAY_AGG: { keywords: ['ORDER BY'] },
    JSON_AGG: { keywords: ['ORDER BY'] },
    JSONB_AGG: { keywords: ['ORDER BY'] },
  },
  mysql: { GROUP_CONCAT: { keywords: ['ORDER BY', 'SEPARATOR'] } },
  sqlite: {},
  oracle: {
    LISTAGG: { keywords: ['ON OVERFLOW ERROR', 'ON OVERFLOW TRUNCATE'], afterComma: true },
  },
  mssql: {},
};

/** True when the call's argument list already has a separating comma at its own
 *  paren depth. Uses the shared tokenizer so a comma inside a nested call, a
 *  string literal or a comment does not count. */
function hasArgumentComma(
  text: string,
  openAt: number,
  offset: number,
  engine: DatabaseEngineId,
): boolean {
  let depth = 0;
  for (const t of tokenize(text, openAt + 1, offset, engine)) {
    if (t.type === 'lparen') depth += 1;
    else if (t.type === 'rparen') depth -= 1;
    else if (t.type === 'comma' && depth === 0) return true;
  }
  return false;
}

/** True when `keyword`'s leading word already appears in the scanned region —
 *  the tail has been written and must not be offered a second time. */
function regionHasKeyword(text: string, from: number, to: number, keyword: string): boolean {
  const head = keyword.split(' ')[0]!;
  return new RegExp(`\\b${head}\\b`, 'i').test(text.slice(from, to));
}

/**
 * Tier-1 hints for the position after an aggregate's first argument, inside
 * the same call (`GROUP_CONCAT(name ▮`). Unlike the argument head these are
 * additions, not a closed set — a comma and another expression stay legal — so
 * the emission promotes rather than suppresses.
 */
export function emitAggregateTailHints(
  out: CompletionCandidate[],
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): void {
  const tails = AGGREGATE_ARG_TAILS[engine];
  if (!tails) return;
  const opts = scanOptionsFor(engine);
  // Only a caret parked after whitespace can start a new keyword here.
  if (!/\s/.test(text[offset - 1] ?? '')) return;
  const scanEnd = offset;
  const region = scanRegion(text, statementStart, scanEnd, opts);
  const openAt = region.openParenAt;
  if (openAt === null) return;
  const inner = scanRegion(text, openAt + 1, scanEnd, opts);
  // The argument must be finished: at least one token, and the last code
  // character is an operand end rather than a comma or a dangling operator.
  if (inner.tokens.length === 0) return;
  const lastChar = text[inner.lastCodeEnd - 1] ?? '';
  if (!/[A-Za-z0-9_)'"`\]]/.test(lastChar)) return;
  // A qualified call (`db.group_concat(`) is a user routine, not the builtin.
  if (isQualifiedCallee(text, openAt, statementStart)) return;
  const entry = tails[wordBefore(text, openAt, statementStart)];
  if (!entry) return;
  // `STRING_AGG(x, ',' ▮)` / `LISTAGG(x, ',' ▮)` — these tails follow the
  // separator argument, so they are illegal until that comma has been typed.
  if (entry.afterComma && !hasArgumentComma(text, openAt, offset, engine)) return;
  for (const kw of entry.keywords) {
    // Do not re-offer a tail the user has already written
    // (`GROUP_CONCAT(name ORDER BY created_at ▮`).
    if (regionHasKeyword(text, openAt, inner.lastCodeEnd, kw)) continue;
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: 'SQL keyword',
    });
  }
}

/** The aggregate whose argument list the cursor sits at the head of, or null.
 *  "Head" means nothing has been typed inside the paren yet — a partial word
 *  still counts, so the hints stay visible while the user types `DIS…`. */
function aggregateAtArgumentHead(
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): string | null {
  const opts = scanOptionsFor(engine);
  const scanEnd = parenHeadScanEnd(text, offset, statementStart);
  if (scanEnd === null) return null;
  const openAt = scanRegion(text, statementStart, scanEnd, opts).openParenAt;
  if (openAt === null) return null;
  if (scanRegion(text, openAt + 1, scanEnd, opts).tokens.length > 0) return null;
  const fn = wordBefore(text, openAt, statementStart);
  if (!fn || !DISTINCT_AGGREGATES[engine]?.has(fn)) return null;
  // A qualified call (`pkg.count(`) is a user routine, not the SQL aggregate.
  return isQualifiedCallee(text, openAt, statementStart) ? null : fn;
}

/** True when the name that owns the `(` at `openAt` is dotted (`pkg.count(`). */
function isQualifiedCallee(text: string, openAt: number, statementStart: number): boolean {
  let k = openAt;
  while (k > statementStart && /\s/.test(text[k - 1] ?? '')) k -= 1;
  while (k > statementStart && /[A-Za-z0-9_]/.test(text[k - 1] ?? '')) k -= 1;
  return text[k - 1] === '.';
}
